import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  Pressable, 
  ScrollView,
  Animated,
  Dimensions,
  Platform
} from 'react-native';
import { theme as baseTheme } from '../styles/theme';
import { Navbar } from '../components/Navbar';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';

// Responsive breakpoints
const getBreakpoint = (width) => {
  if (width < 480) return 'mobile';
  if (width < 768) return 'mobileLarge';
  if (width < 1024) return 'tablet';
  if (width < 1440) return 'desktop';
  return 'desktopLarge';
};

// Set page title
if (isWeb && typeof document !== 'undefined') {
  document.title = 'Home - SheetShed';
}

// Staccato cursor dot
const StaccatoCursor = () => {
  const [position, setPosition] = useState({ x: -100, y: -100 });

  useEffect(() => {
    if (!isWeb) return;
    const handleMouseMove = (e) => setPosition({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', handleMouseMove);
    document.body.style.cursor = 'none';
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.style.cursor = 'auto';
    };
  }, []);

  if (!isWeb) return null;

  return (
    <div style={{
      position: 'fixed', left: position.x - 4, top: position.y - 4,
      width: 8, height: 8, borderRadius: '50%', backgroundColor: '#C75B7A',
      pointerEvents: 'none', zIndex: 10000,
      boxShadow: '0 0 12px rgba(199, 91, 122, 0.5)',
    }} />
  );
};

// Musical decoration lines
const MusicalDecorations = ({ isDark }) => {
  if (!isWeb) return null;
  const theme = isDark ? baseTheme.dark : baseTheme.light;
  
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {/* Flowing gradient background */}
      <div style={{
        position: 'absolute', width: '150%', height: '200%',
        background: `
          radial-gradient(ellipse 600px 400px at 20% 10%, ${theme.primary}12 0%, transparent 50%),
          radial-gradient(ellipse 500px 350px at 80% 25%, ${theme.primaryLight}10 0%, transparent 50%),
          radial-gradient(ellipse 700px 500px at 10% 50%, ${theme.primary}06 0%, transparent 50%),
          radial-gradient(ellipse 600px 400px at 90% 60%, ${theme.primaryLight}12 0%, transparent 50%)
        `,
        animation: 'snakeFlow 25s ease-in-out infinite',
      }} />
      
      {/* Musical staff lines */}
      <svg style={{ position: 'absolute', top: '15%', left: '-5%', width: '110%', height: '200px', opacity: 0.04 }}>
        <path d="M0,20 Q200,60 400,20 T800,20 T1200,20 T1600,20" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,50 Q200,90 400,50 T800,50 T1200,50 T1600,50" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,80 Q200,120 400,80 T800,80 T1200,80 T1600,80" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,110 Q200,150 400,110 T800,110 T1200,110 T1600,110" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,140 Q200,180 400,140 T800,140 T1200,140 T1600,140" stroke={theme.primary} fill="none" strokeWidth="1.5" />
      </svg>
      
      <svg style={{ position: 'absolute', top: '55%', right: '-5%', width: '110%', height: '150px', opacity: 0.03, transform: 'rotate(2deg)' }}>
        <path d="M0,20 Q300,50 600,20 T1200,20 T1800,20" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,45 Q300,75 600,45 T1200,45 T1800,45" stroke={theme.primary} fill="none" strokeWidth="1.5" />
        <path d="M0,70 Q300,100 600,70 T1200,70 T1800,70" stroke={theme.primary} fill="none" strokeWidth="1.5" />
      </svg>
      
      <style>{`
        @keyframes snakeFlow {
          0%, 100% { transform: translate(0, 0) rotate(0deg); }
          25% { transform: translate(-3%, 8%) rotate(1deg); }
          50% { transform: translate(3%, -3%) rotate(-0.5deg); }
          75% { transform: translate(-2%, 4%) rotate(0.5deg); }
        }
      `}</style>
    </div>
  );
};

// Animated text
const AnimatedText = ({ children, style, delay = 0, type = 'slideUp' }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(type === 'slideUp' ? 40 : type === 'slideDown' ? -40 : 0)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 12, tension: 60, useNativeDriver: true }),
      ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Animated.Text style={[style, { opacity, transform: [{ translateY }] }]}>
      {children}
    </Animated.Text>
  );
};

// Animated View wrapper
const AnimatedSection = ({ children, delay = 0, style, direction = 'up' }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(direction === 'up' ? 60 : direction === 'down' ? -60 : 0)).current;
  const scale = useRef(new Animated.Value(0.96)).current;

  useEffect(() => {
    const timer = setTimeout(() => {
    Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 700, useNativeDriver: true }),
        Animated.spring(translateY, { toValue: 0, friction: 10, tension: 50, useNativeDriver: true }),
        Animated.spring(scale, { toValue: 1, friction: 10, tension: 40, useNativeDriver: true }),
    ]).start();
    }, delay);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Animated.View style={[style, { opacity, transform: [{ translateY }, { scale }] }]}>
      {children}
    </Animated.View>
  );
};

// Button with hover effect and SUBTLE shimmer
const AnimatedButton = ({ children, onPress, style, textStyle, shimmer = false, theme }) => {
  const scale = useRef(new Animated.Value(1)).current;

  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => Animated.spring(scale, { toValue: 0.96, useNativeDriver: true }).start()}
      onPressOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
      onHoverIn={() => Animated.spring(scale, { toValue: 1.03, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[style, { transform: [{ scale }], position: 'relative' }]}>
        {shimmer && isWeb && (
          <>
            <div className="shimmer-border" style={{
              position: 'absolute',
              top: -1, left: -1, right: -1, bottom: -1,
              borderRadius: 999,
              zIndex: -1,
              background: `linear-gradient(90deg, 
                transparent 0%, 
                transparent 40%, 
                rgba(255,255,255,0.3) 50%, 
                transparent 60%, 
                transparent 100%
              )`,
              backgroundSize: '300% 100%',
              animation: 'subtleShimmer 4s ease-in-out infinite',
            }} />
            <style>{`
              @keyframes subtleShimmer {
                0% { background-position: 100% 0; }
                100% { background-position: -100% 0; }
              }
            `}</style>
          </>
        )}
        {children}
      </Animated.View>
    </Pressable>
  );
};

// Full sheet music preview with proper notation (no window dots)
const SheetMusicPreview = ({ theme, title, rotation, screenWidth }) => {
  const floatAnim = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(1)).current;
  const breakpoint = getBreakpoint(screenWidth);

  useEffect(() => {
    Animated.loop(Animated.sequence([
      Animated.timing(floatAnim, { toValue: -8, duration: 3000, useNativeDriver: true }),
      Animated.timing(floatAnim, { toValue: 8, duration: 3000, useNativeDriver: true }),
    ])).start();
  }, []);

  // Musical measures with more realistic notes
  const measures = [
    [
      { beat: 0.2, line: 3, type: 'quarter' },
      { beat: 1.2, line: 5, type: 'eighth' },
      { beat: 1.8, line: 4, type: 'eighth' },
      { beat: 2.5, line: 2, type: 'quarter' },
      { beat: 3.5, line: 3, type: 'quarter' },
    ],
    [
      { beat: 0.3, line: 4, type: 'half' },
      { beat: 2.2, line: 1, type: 'quarter' },
      { beat: 3.2, line: 3, type: 'eighth' },
      { beat: 3.7, line: 5, type: 'eighth' },
    ],
    [
      { beat: 0.5, line: 2, type: 'quarter' },
      { beat: 1.5, line: 4, type: 'quarter' },
      { beat: 2.3, line: 3, type: 'eighth' },
      { beat: 2.8, line: 1, type: 'eighth' },
      { beat: 3.4, line: 5, type: 'quarter' },
    ],
    [
      { beat: 0.3, line: 3, type: 'quarter' },
      { beat: 1.3, line: 2, type: 'quarter' },
      { beat: 2.3, line: 4, type: 'half' },
    ],
    [
      { beat: 0.4, line: 5, type: 'eighth' },
      { beat: 0.9, line: 4, type: 'eighth' },
      { beat: 1.5, line: 2, type: 'quarter' },
      { beat: 2.5, line: 3, type: 'whole' },
    ],
  ];

  const sheetWidth = breakpoint === 'mobile' ? 280 : breakpoint === 'mobileLarge' ? 320 : 380;
  const sheetHeight = breakpoint === 'mobile' ? 400 : breakpoint === 'mobileLarge' ? 460 : 540;

        return (
    <Pressable
      onHoverIn={() => Animated.spring(scale, { toValue: 1.02, useNativeDriver: true }).start()}
      onHoverOut={() => Animated.spring(scale, { toValue: 1, useNativeDriver: true }).start()}
    >
      <Animated.View style={[styles.sheetPreview, { 
        backgroundColor: theme.surface, 
        width: sheetWidth, 
        height: sheetHeight, 
        transform: [{ translateY: floatAnim }, { rotate: rotation }, { scale }] 
      }]}>
        {/* Clean header with just centered title */}
        <View style={[styles.sheetHeader, { borderBottomColor: theme.borderLight }]}>
          <Text style={[styles.sheetTitleCentered, { color: theme.textPrimary }]}>{title}</Text>
        </View>
        
        {/* Sheet content with treble clef, time sig, and notes */}
        <View style={styles.sheetContent}>
          {/* Treble clef */}
          <View style={styles.clefArea}>
            <Text style={[styles.clefSymbol, { color: theme.textMuted }]}>𝄞</Text>
            <View style={styles.timeSig}>
              <Text style={[styles.timeSigText, { color: theme.textMuted }]}>4</Text>
              <Text style={[styles.timeSigText, { color: theme.textMuted }]}>4</Text>
            </View>
          </View>
          
          {/* Staff with measures */}
          {measures.map((measure, measureIdx) => (
            <View key={measureIdx} style={styles.measure}>
              {/* 5 staff lines */}
              {[0, 1, 2, 3, 4].map((lineIdx) => (
                <View key={lineIdx} style={[styles.staffLine, { backgroundColor: theme.border }]} />
              ))}
              
              {/* Notes */}
              {measure.map((note, noteIdx) => {
                const xPos = 8 + (note.beat * 22);
                const yPos = 40 - (note.line * 8);
                const isHollow = note.type === 'half' || note.type === 'whole';
                
        return (
                  <View key={noteIdx} style={[styles.noteGroup, { left: `${xPos}%`, top: yPos }]}>
                    <View style={[
                      styles.noteHead,
                      { 
                        backgroundColor: isHollow ? 'transparent' : theme.primary,
                        borderColor: theme.primary,
                        borderWidth: isHollow ? 2 : 0,
                        width: note.type === 'whole' ? 16 : 12,
                        height: note.type === 'whole' ? 12 : 10,
                      }
                    ]} />
                    {note.type !== 'whole' && (
                      <View style={[styles.noteStem, { backgroundColor: theme.primary }]} />
                    )}
                    {note.type === 'eighth' && (
                      <View style={[styles.noteFlag, { backgroundColor: theme.primary }]} />
                    )}
          </View>
        );
              })}
              
              {/* Bar line */}
              {measureIdx < measures.length - 1 && (
                <View style={[styles.barLine, { backgroundColor: theme.border }]} />
              )}
            </View>
          ))}
          </View>
    </Animated.View>
    </Pressable>
  );
};

// Scroll-triggered animation wrapper
const ScrollReveal = ({ children, style, delay = 0 }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(50)).current;
  const [hasAnimated, setHasAnimated] = useState(false);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!isWeb || hasAnimated) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          setTimeout(() => {
            Animated.parallel([
              Animated.timing(opacity, { toValue: 1, duration: 600, useNativeDriver: true }),
              Animated.spring(translateY, { toValue: 0, friction: 12, tension: 50, useNativeDriver: true }),
            ]).start();
          }, delay);
        }
      },
      { threshold: 0.15 }
    );

    if (viewRef.current) observer.observe(viewRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  if (!isWeb) {
    return <View style={style}>{children}</View>;
  }

  return (
    <Animated.View 
      ref={viewRef} 
      style={[style, { opacity, transform: [{ translateY }] }]}
    >
      {children}
    </Animated.View>
  );
};

// Step badge
const StepBadge = ({ number, theme }) => {
  const pulse = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    Animated.loop(Animated.sequence([
      Animated.timing(pulse, { toValue: 1.12, duration: 1500, useNativeDriver: true }),
      Animated.timing(pulse, { toValue: 1, duration: 1500, useNativeDriver: true }),
    ])).start();
  }, []);

  return (
    <Animated.View style={[styles.stepBadge, { backgroundColor: theme.primary, transform: [{ scale: pulse }] }]}>
      <Text style={[styles.stepNumber, { color: theme.textOnPrimary }]}>{number}</Text>
    </Animated.View>
  );
};

export const LandingScreen = ({ navigation }) => {
  const [isDark, setIsDark] = useState(false);
  const [screenWidth, setScreenWidth] = useState(SCREEN_WIDTH);
  const scrollY = useRef(new Animated.Value(0)).current;
  const scrollViewRef = useRef(null);

  const theme = isDark ? baseTheme.dark : baseTheme.light;
  const breakpoint = getBreakpoint(screenWidth);
  const isMobile = breakpoint === 'mobile' || breakpoint === 'mobileLarge';
  const isTablet = breakpoint === 'tablet';

  // Load dark mode preference from localStorage
  useEffect(() => {
    if (isWeb && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('sheetshed_darkmode');
      if (saved === 'true') setIsDark(true);
    }
  }, []);

  // Save dark mode preference when changed
  const toggleDarkMode = (value) => {
    setIsDark(value);
    if (isWeb && typeof localStorage !== 'undefined') {
      localStorage.setItem('sheetshed_darkmode', String(value));
    }
  };

  useEffect(() => {
    if (!isWeb) return;
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToSection = useCallback((section) => {
    const positions = { features: screenWidth < 768 ? 700 : 900, how: screenWidth < 768 ? 1500 : 1800, cta: screenWidth < 768 ? 2200 : 2600 };
    scrollViewRef.current?.scrollTo({ y: positions[section] || 0, animated: true });
  }, [screenWidth]);

  const getPadding = () => {
    if (breakpoint === 'mobile') return 20;
    if (breakpoint === 'mobileLarge') return 28;
    if (breakpoint === 'tablet') return 40;
    return 80;
  };

  const getHeroFontSize = () => {
    if (breakpoint === 'mobile') return 44;
    if (breakpoint === 'mobileLarge') return 52;
    if (breakpoint === 'tablet') return 60;
    if (breakpoint === 'desktop') return 72;
    return 84;
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.background }]}>
      <StaccatoCursor />
      <MusicalDecorations isDark={isDark} />

      <Navbar 
        navigation={navigation} 
        scrollY={scrollY} 
        isDark={isDark} 
        toggleDarkMode={toggleDarkMode} 
        currentRoute="Landing"
        scrollToSection={scrollToSection} 
        screenWidth={screenWidth} 
      />

      <Animated.ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: true })} scrollEventThrottle={16}>
        
        {/* Hero Section */}
        <AnimatedSection style={[styles.hero, { paddingHorizontal: getPadding(), flexDirection: isMobile ? 'column' : 'row', paddingVertical: isMobile ? 60 : 100, minHeight: isMobile ? 'auto' : SCREEN_HEIGHT * 0.9 }]} delay={0}>
          <View style={[styles.heroContent, { maxWidth: isMobile ? '100%' : 600 }]}>
            <AnimatedText style={[styles.heroTagline, { color: theme.primary, fontSize: isMobile ? 16 : 20 }]} delay={100} type="slideDown">
              Your Music, Your Way
            </AnimatedText>
            
            <View style={styles.heroTitleContainer}>
              <AnimatedText style={[styles.heroTitle, { color: theme.textPrimary, fontSize: getHeroFontSize(), lineHeight: getHeroFontSize() * 1.1 }]} delay={200}>
                Sheet music,
              </AnimatedText>
              <AnimatedText style={[styles.heroTitleAccent, { color: theme.primary, fontSize: getHeroFontSize(), lineHeight: getHeroFontSize() * 1.1 }]} delay={350}>
                simplified
              </AnimatedText>
      </View>

            <AnimatedText style={[styles.heroDescription, { color: theme.textSecondary, fontSize: isMobile ? 17 : 20, maxWidth: isMobile ? '100%' : 480 }]} delay={500}>
              Upload, organize, and display your sheet music with ease. Auto page-turn keeps you focused on playing.
            </AnimatedText>

            <AnimatedSection delay={650} style={[styles.heroCTA, { flexDirection: isMobile ? 'column' : 'row' }]}>
              <AnimatedButton onPress={() => navigation.navigate('Auth', { mode: 'signup' })} style={[styles.ctaButtonPrimary, { backgroundColor: theme.primary }]} shimmer={true} theme={theme}>
                <Text style={[styles.ctaButtonPrimaryText, { color: theme.textOnPrimary, fontSize: isMobile ? 17 : 18 }]}>Start for free</Text>
              </AnimatedButton>
              <AnimatedButton onPress={() => navigation.navigate('Plans')} style={[styles.ctaButtonSecondary, { borderColor: theme.border }]} theme={theme}>
                <Text style={[styles.ctaButtonSecondaryText, { color: theme.textSecondary, fontSize: isMobile ? 17 : 18 }]}>View pricing</Text>
              </AnimatedButton>
            </AnimatedSection>
      </View>

          <View style={[styles.heroVisual, { width: isMobile ? '100%' : isTablet ? 380 : 500, height: isMobile ? 440 : 600, marginTop: isMobile ? 50 : 0 }]}>
            <SheetMusicPreview theme={theme} title="Moonlight Sonata" rotation="-4deg" screenWidth={screenWidth} />
            {!isMobile && (
              <View style={styles.secondSheet}>
                <SheetMusicPreview theme={theme} title="Clair de Lune" rotation="3deg" screenWidth={screenWidth} />
              </View>
            )}
          </View>
        </AnimatedSection>

      {/* Features Section - Clean & Simple */}
        <ScrollReveal style={[styles.featuresSection, { paddingHorizontal: getPadding() }]}>
          <Text style={[styles.sectionLabel, { color: theme.primary }]}>WHY SHEETSHED</Text>
          <Text style={[styles.sectionTitle, { color: theme.textPrimary, fontSize: isMobile ? 28 : isTablet ? 36 : 44 }]}>
            Everything you need, nothing you don't
        </Text>
        
          <View style={[styles.featuresGrid, { flexDirection: isMobile ? 'column' : 'row' }]}>
            {[
              { icon: 'upload', title: 'Drag and drop', desc: 'Simply drop your PDFs or images. We handle the rest.' },
              { icon: 'search', title: 'Smart organization', desc: 'Find any piece with tags, search, and favorites.' },
              { icon: 'music', title: 'Auto page turn', desc: 'Set your tempo and let the music flow.' },
            ].map((feature, i) => (
              <ScrollReveal key={i} delay={i * 100} style={[styles.featureCard, { backgroundColor: theme.surface }]}>
                <View style={[styles.featureIconBox, { backgroundColor: theme.primary }]}>
                  <Text style={[styles.featureIconText, { color: theme.textOnPrimary }]}>
                    {feature.icon === 'upload' ? '+' : feature.icon === 'search' ? 'S' : 'M'}
                  </Text>
                </View>
                <Text style={[styles.featureTitle, { color: theme.textPrimary }]}>{feature.title}</Text>
                <Text style={[styles.featureDesc, { color: theme.textSecondary }]}>{feature.desc}</Text>
              </ScrollReveal>
            ))}
        </View>
        </ScrollReveal>

        {/* Free vs Pro */}
        <ScrollReveal style={[styles.pricingPreview, { paddingHorizontal: getPadding() }]}>
          <View style={[styles.pricingCard, { backgroundColor: theme.surface, flexDirection: isMobile ? 'column' : 'row', padding: isMobile ? 28 : 40 }]}>
            <View style={styles.pricingTier}>
              <Text style={[styles.tierLabel, { color: theme.textMuted }]}>FREE</Text>
              <Text style={[styles.tierValue, { color: theme.textPrimary, fontSize: isMobile ? 28 : 36 }]}>20 sheets</Text>
      </View>
            <View style={[styles.pricingDivider, { backgroundColor: theme.border, width: isMobile ? '100%' : 2, height: isMobile ? 2 : 60, marginVertical: isMobile ? 20 : 0, marginHorizontal: isMobile ? 0 : 48 }]} />
            <View style={styles.pricingTier}>
              <Text style={[styles.tierLabel, { color: theme.primary }]}>PRO</Text>
              <Text style={[styles.tierValue, { color: theme.textPrimary, fontSize: isMobile ? 28 : 36 }]}>Unlimited</Text>
            </View>
          </View>
          <AnimatedButton onPress={() => navigation.navigate('Plans')} style={[styles.pricingButton, { backgroundColor: theme.primary }]} theme={theme}>
            <Text style={[styles.pricingButtonText, { color: theme.textOnPrimary }]}>See all plans</Text>
          </AnimatedButton>
        </ScrollReveal>

        {/* How it Works */}
        <ScrollReveal style={[styles.howSection, { paddingHorizontal: getPadding() }]}>
          <View style={[styles.howCard, { backgroundColor: theme.surfaceDark, padding: isMobile ? 32 : 60 }]}>
            <Text style={[styles.howLabel, { color: theme.primaryLight, fontSize: isMobile ? 13 : 15 }]}>HOW IT WORKS</Text>
            <Text style={[styles.howTitle, { color: theme.textOnDark, fontSize: isMobile ? 28 : isTablet ? 36 : 44, marginBottom: isMobile ? 32 : 48 }]}>
              Three simple steps
            </Text>
            
            <View style={[styles.stepsContainer, { flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 28 : 40 }]}>
              {[
                { num: '1', title: 'Upload your sheets', desc: 'Drag PDFs or images into your library' },
                { num: '2', title: 'Organize your way', desc: 'Add tags, create collections, mark favorites' },
                { num: '3', title: 'Play with ease', desc: 'Full screen display with auto page turn' },
              ].map((step, i) => (
                <ScrollReveal key={i} delay={i * 120} style={[styles.stepCard, { flex: isMobile ? undefined : 1 }]}>
                  <StepBadge number={step.num} theme={theme} />
                  <Text style={[styles.stepTitle, { color: theme.textOnDark, fontSize: isMobile ? 18 : 20 }]}>{step.title}</Text>
                  <Text style={[styles.stepDesc, { color: theme.textOnDarkMuted, fontSize: isMobile ? 15 : 16 }]}>{step.desc}</Text>
                </ScrollReveal>
              ))}
            </View>
          </View>
        </ScrollReveal>

        {/* CTA Section */}
        <ScrollReveal style={[styles.ctaSection, { paddingHorizontal: getPadding() }]}>
          <View style={[styles.ctaCard, { backgroundColor: theme.accent, padding: isMobile ? 40 : 70 }]}>
            <Text style={[styles.ctaTitle, { color: theme.textPrimary, fontSize: isMobile ? 28 : isTablet ? 36 : 44 }]}>
              Ready to simplify your practice?
            </Text>
            <Text style={[styles.ctaSubtitle, { color: theme.textSecondary, fontSize: isMobile ? 17 : 19 }]}>
              Join musicians who have made sheet music effortless
            </Text>
            <AnimatedButton onPress={() => navigation.navigate('Auth', { mode: 'signup' })} style={[styles.ctaMainButton, { backgroundColor: theme.secondary }]} shimmer={true} theme={theme}>
              <Text style={[styles.ctaMainButtonText, { color: theme.textOnDark, fontSize: isMobile ? 17 : 18 }]}>Create free account</Text>
            </AnimatedButton>
          </View>
        </ScrollReveal>

      {/* Footer */}
        <View style={[styles.footer, { borderTopColor: theme.borderLight, paddingHorizontal: getPadding() }]}>
          <View style={styles.footerLogoContainer}>
            <View style={[styles.footerLogoIcon, { backgroundColor: theme.primary }]}>
              <View style={[styles.logoNote, { backgroundColor: theme.textOnPrimary }]} />
              <View style={[styles.logoNote2, { backgroundColor: theme.textOnPrimary }]} />
            </View>
            <Text style={[styles.footerLogoText, { color: theme.textPrimary }]}>SheetShed</Text>
          </View>
          <Text style={[styles.footerText, { color: theme.textMuted }]}>Made for musicians, by musicians</Text>
        </View>
      </Animated.ScrollView>
      </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollView: { flex: 1 },
  scrollContent: { paddingTop: 120 },

  // Navbar
  navbarContainer: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100, paddingTop: 20 },
  navbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderRadius: baseTheme.borderRadius.pill, paddingHorizontal: 24 },
  logoContainer: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  logoIcon: { width: 40, height: 40, borderRadius: baseTheme.borderRadius.lg, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  logoNote: { width: 9, height: 9, borderRadius: 5, position: 'absolute', top: 11, left: 11 },
  logoNote2: { width: 7, height: 7, borderRadius: 4, position: 'absolute', bottom: 11, right: 11 },
  logoText: { fontSize: 20, fontWeight: baseTheme.typography.fontWeight.bold },
  navLinks: { flexDirection: 'row', gap: 6 },
  navPill: { paddingHorizontal: 18, paddingVertical: 12, borderRadius: baseTheme.borderRadius.pill, position: 'relative', overflow: 'hidden' },
  navPillBg: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: baseTheme.borderRadius.pill },
  navPillText: { fontSize: 15, fontWeight: baseTheme.typography.fontWeight.medium, position: 'relative', zIndex: 1 },
  navAuth: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  darkModeToggle: { width: 40, height: 40, borderRadius: 20, justifyContent: 'center', alignItems: 'center' },
  signInButton: { paddingHorizontal: 18, paddingVertical: 12 },
  signInText: { fontSize: 15, fontWeight: baseTheme.typography.fontWeight.medium },
  signUpButton: { paddingHorizontal: 22, paddingVertical: 12, borderRadius: baseTheme.borderRadius.pill },
  signUpText: { fontSize: 15, fontWeight: baseTheme.typography.fontWeight.semibold },

  // Hero
  hero: { alignItems: 'center', justifyContent: 'center', gap: 50, position: 'relative' },
  heroContent: { flex: 1, zIndex: 2 },
  heroTagline: { fontWeight: baseTheme.typography.fontWeight.bold, marginBottom: 20, letterSpacing: 1, textTransform: 'uppercase' },
  heroTitleContainer: { marginBottom: 28 },
  heroTitle: { fontWeight: baseTheme.typography.fontWeight.bold },
  heroTitleAccent: { fontWeight: baseTheme.typography.fontWeight.bold },
  heroDescription: { lineHeight: 32, marginBottom: 44 },
  heroCTA: { gap: 18, alignItems: 'stretch' },
  ctaButtonPrimary: { paddingHorizontal: 36, paddingVertical: 20, borderRadius: baseTheme.borderRadius.pill, alignItems: 'center' },
  ctaButtonPrimaryText: { fontWeight: baseTheme.typography.fontWeight.semibold },
  ctaButtonSecondary: { paddingHorizontal: 36, paddingVertical: 20, borderRadius: baseTheme.borderRadius.pill, borderWidth: 2, alignItems: 'center' },
  ctaButtonSecondaryText: { fontWeight: baseTheme.typography.fontWeight.medium },

  // Sheet Preview - Clean design without window dots
  heroVisual: { position: 'relative' },
  secondSheet: { position: 'absolute', top: 70, left: 90 },
  sheetPreview: { 
    borderRadius: baseTheme.borderRadius.xxl, 
    overflow: 'hidden', 
    ...Platform.select({ web: { boxShadow: baseTheme.shadows.xl } }) 
  },
  sheetHeader: { 
    paddingVertical: 16,
    paddingHorizontal: 20, 
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  sheetTitleCentered: { 
    fontSize: 16, 
    fontWeight: '600', 
    textAlign: 'center',
  },
  sheetContent: { 
    flex: 1,
    paddingVertical: 20,
    paddingHorizontal: 16,
  },
  clefArea: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    paddingLeft: 4,
  },
  clefSymbol: { fontSize: 48 },
  timeSig: { marginLeft: 8 },
  timeSigText: { fontSize: 18, fontWeight: '700', lineHeight: 20 },
  measure: { 
    height: 56, 
    marginBottom: 16, 
    position: 'relative', 
    justifyContent: 'space-between',
    marginLeft: 60,
  },
  staffLine: { height: 1.5, borderRadius: 1 },
  noteGroup: { position: 'absolute' },
  noteHead: { borderRadius: 8 },
  noteStem: { width: 2, height: 32, position: 'absolute', right: 0, bottom: 8, borderRadius: 1 },
  noteFlag: { width: 8, height: 12, position: 'absolute', right: -6, bottom: 36, borderRadius: 3 },
  barLine: { position: 'absolute', right: 0, top: 0, bottom: 0, width: 2 },

  // Features - Clean & Simple
  featuresSection: { paddingVertical: 80, maxWidth: 1100, alignSelf: 'center', width: '100%' },
  sectionLabel: { fontSize: 14, fontWeight: '600', letterSpacing: 2, textAlign: 'center', marginBottom: 12 },
  sectionTitle: { fontWeight: '700', textAlign: 'center', marginBottom: 48 },
  featuresGrid: { gap: 20, width: '100%' },
  featureCard: { flex: 1, padding: 28, borderRadius: 20, minWidth: 260, ...Platform.select({ web: { boxShadow: '0 2px 12px rgba(0,0,0,0.04)' } }) },
  featureIcon: { fontSize: 32, marginBottom: 16 },
  featureIconBox: { width: 48, height: 48, borderRadius: 14, justifyContent: 'center', alignItems: 'center', marginBottom: 16 },
  featureIconText: { fontSize: 22, fontWeight: '700' },
  featureTitle: { fontSize: 20, fontWeight: '700', marginBottom: 8 },
  featureDesc: { fontSize: 15, lineHeight: 24 },

  // Pricing Preview
  pricingPreview: { paddingBottom: 60, alignItems: 'center' },
  pricingCard: { borderRadius: 24, maxWidth: 600, width: '100%', alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { boxShadow: '0 2px 16px rgba(0,0,0,0.06)' } }) },
  pricingTier: { alignItems: 'center', paddingHorizontal: 8 },
  tierLabel: { fontSize: 13, fontWeight: '700', marginBottom: 6, letterSpacing: 1.5 },
  tierValue: { fontWeight: '700' },
  pricingDivider: {},
  pricingButton: { marginTop: 24, paddingHorizontal: 32, paddingVertical: 16, borderRadius: 999 },
  pricingButtonText: { fontSize: 16, fontWeight: '600' },

  // How Section
  howSection: { paddingVertical: 60 },
  howCard: { borderRadius: baseTheme.borderRadius.xxxl, maxWidth: 1200, alignSelf: 'center', width: '100%', overflow: 'hidden' },
  howLabel: { fontWeight: baseTheme.typography.fontWeight.bold, letterSpacing: 2, marginBottom: 16 },
  howTitle: { fontWeight: baseTheme.typography.fontWeight.bold },
  stepsContainer: { width: '100%' },
  stepCard: { alignItems: 'flex-start' },
  stepBadge: { width: 48, height: 48, borderRadius: 24, justifyContent: 'center', alignItems: 'center', marginBottom: 20 },
  stepNumber: { fontSize: 20, fontWeight: baseTheme.typography.fontWeight.bold },
  stepTitle: { fontWeight: baseTheme.typography.fontWeight.bold, marginBottom: 8 },
  stepDesc: { lineHeight: 26 },

  // CTA Section
  ctaSection: { paddingVertical: 100 },
  ctaCard: { borderRadius: baseTheme.borderRadius.xxxl, alignItems: 'center', maxWidth: 900, alignSelf: 'center', width: '100%' },
  ctaTitle: { fontWeight: baseTheme.typography.fontWeight.bold, textAlign: 'center', marginBottom: 20 },
  ctaSubtitle: { textAlign: 'center', marginBottom: 36, maxWidth: 450 },
  ctaMainButton: { paddingHorizontal: 40, paddingVertical: 20, borderRadius: baseTheme.borderRadius.pill },
  ctaMainButtonText: { fontWeight: baseTheme.typography.fontWeight.semibold },

  // Footer
  footer: { paddingVertical: 50, alignItems: 'center', gap: 18, borderTopWidth: 1 },
  footerLogoContainer: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  footerLogoIcon: { width: 36, height: 36, borderRadius: baseTheme.borderRadius.md, justifyContent: 'center', alignItems: 'center', position: 'relative' },
  footerLogoText: { fontSize: 18, fontWeight: baseTheme.typography.fontWeight.bold },
  footerText: { fontSize: 15 },
});

export default LandingScreen;
